<script>
  import { debounce } from 'lodash';
  import rules from '@/helpers/validation-rules';
  import GroupSelect from '@/components/form/GroupSelect.vue';
  import RedemptionRequirements from '@/components/dialogs/RedemptionRequirements.vue';
  import RedemptionSubmit from '@/components/dialogs/RedemptionSubmit.vue';
  import ExclamationIcon from '@/assets/images/svg/icons/icon-exclamation.svg';
  import ChatIcon from '@/assets/images/svg/icons/chat.svg';
  import InformationCircle from '@/assets/images/svg/icons/information-circle.svg';

  export default {
    name: 'Redeem',

    components: {
      RedemptionSubmit,
      RedemptionRequirements,
      GroupSelect,
      ExclamationIcon,
      ChatIcon,
      InformationCircle,
    },

    props: {
      currencies: {
        required: true,
      },
    },

    data: () => ({
      rules,
      requirementsDialog: false,
      estimatedAmount: '',
      estimatedAmountInUSD: '',
      estimatedFeeAmountInUSD: '',
      estimatedFeeAmount: '',
      selectedCurrency: '',
      model: {
        address: '',
        amount: '',
      },
      estimateLoading: false,
      showRedemptionDialog: false,
      isFormValid: false,
      requestIsPending: false,
    }),

    computed: {
      totalWithdrawals() {
        return this.$store.getters.getUserTotalWithdrawalRequests;
      },
      maxCoinsAmountText() {
        return this.isKycApproved
          ? '$ Enter Amount'
          : `$ Enter Amount (KYC not completed, redeemed $${this.totalWithdrawals / 100} / ${this.maxAmountWithoutKyc})`;
      },
      isKycApproved() {
        return this.$store.getters.isKycApproved;
      },
      userMMCBalance() {
        return this.$store.getters.getUserWithdrawableBalance?.balance?.MMC || 0;
      },
      formattedMMCBalance() {
        return (parseInt(this.userMMCBalance) / 100).toLocaleString('en-US');
      },
      maxBalance() {
        return (value) =>
          value <= this.userMMCBalance / 100 || `Your vault’s balance is insufficient for this redemption.`;
      },
      kycError() {
        return (value) => {
          const totalWithdrawals = this.totalWithdrawals / 100;
          const maxWithoutKyc = this.maxAmountWithoutKyc;

          if (totalWithdrawals + Number(value) > maxWithoutKyc && !this.isKycApproved) {
            return `You need to complete KYC to redeem more than $${maxWithoutKyc}`;
          } else {
            return true;
          }
        };
      },
      addressValidation() {
        switch (this.selectedCurrency.value) {
          case 'BTC':
            return /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/;
          case 'ETH':
          case 'USDTERC20':
          case 'USDTSOL':
          case 'USDTBSC':
          case 'USDC':
            return /^0x[a-fA-F0-9]{40}$/;
          case 'USDTTRC20':
            return /^T[a-zA-Z1-9]{33}$/;
          default:
            return /.*/;
        }
      },
      isAmountValid() {
        const amount = Number(this.model.amount);
        return amount >= this.minAmount && amount <= this.userMMCBalance / 100 && this.kycError(amount) === true;
      },
      isWithdrawButtonDisabled() {
        return (
          !this.isFormValid ||
          !this.isAmountValid ||
          this.requestIsPending ||
          !this.model.address ||
          !this.selectedCurrency
        );
      },
      minAmount() {
        return this.$store.getters.getGlobalAffiliateConfig?.minAmountWithdraw;
      },
      maxAmountWithoutKyc() {
        return this.$store.getters.getGlobalConfig.maxAmountWithoutKyc;
      },
      filteredCurrencies() {
        return this.currencies.filter((group) => group.text === 'Crypto');
      },
    },

    watch: {
      'model.amount': {
        async handler(value) {
          if (!value || value < 0) {
            await this.$nextTick();
            this.model.amount = 0;
          } else {
            this.loadEstimate();
          }
        },
      },
    },

    async mounted() {
      await Promise.all([
        this.$store.dispatch('loadGlobalAffiliateConfig'),
        this.$store.dispatch('loadGlobalConfig'),
        this.$store.dispatch('getTotalWithdrawalRequests'),
      ]);
    },

    methods: {
      async submit() {
        if (!this.$refs.form.validate() && this.requestIsPending) {
          return;
        }

        this.requestIsPending = true;

        try {
          const { data } = await this.$store.dispatch('requestWithdrawal', {
            send_address: this.model.address,
            amount: this.model.amount,
            payment_currency: this.selectedCurrency.value,
          });

          if (data?.haserror) {
            await this.$store.dispatch('showConfirmDialog', {
              title: '',
              text: data?.status || 'Something went wrong, please try again later',
              yesText: 'OK',
              noVisible: false,
              callback: () => {
                this.$store.dispatch('hideConfirmDialog');
              },
            });

            return;
          }
          this.$store.dispatch('getTotalWithdrawalRequests');
          this.showRedemptionDialog = true;

          this.$refs.form.reset();
          this.selectedCurrency = '';
        } catch (e) {
          await this.$store.dispatch('showConfirmDialog', {
            title: '',
            text: e.response.data?.status || 'Something went wrong, please try again later',
            yesText: 'OK',
            noVisible: false,
            callback: () => {
              this.$store.dispatch('hideConfirmDialog');
            },
          });
          console.error(e);
        } finally {
          this.requestIsPending = false;
        }
      },

      getEstimate: debounce(async function () {
        if (!this.model.amount) {
          return;
        }

        this.estimateLoading = true;

        try {
          const { data } = await this.$store.dispatch('getEstimatePayout', {
            amount: this.model.amount,
            currency: this.selectedCurrency.value,
          });

          if (!data.data.haserror) {
            this.estimatedAmount = data.data.amount_paid_to_user;
            this.estimatedFeeAmount = data.data.redemption_fee;
            this.estimatedFeeAmountInUSD = data.data.redemption_fee_in_usd?.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
            this.estimatedAmountInUSD = data.data.amount_paid_to_user_in_usd
              ?.toString()
              .match(/^-?\d+(?:\.\d{0,2})?/)[0];
          }
        } catch (e) {
          console.error(e);
          await this.$store.dispatch('showAlertSnackBar', {
            text: 'Request Failed. Try again later.',
          });
        }

        this.estimateLoading = false;
      }, 300),

      loadEstimate() {
        if (this.selectedCurrency && this.isAmountValid) {
          this.getEstimate();
        }
      },

      selectCurrency() {
        this.loadEstimate();

        if (this.$refs.addressInput) {
          this.$refs.addressInput.validate();
        }
      },

      setAllBalance() {
        this.model.amount = this.userMMCBalance / 100;
      },

      openChat() {
        if (typeof window.$zoho === 'undefined') return;

        $zoho.salesiq.floatbutton?.visible('show');
        $zoho.salesiq?.floatwindow?.open();
      },
    },
  };
</script>

<template>
  <div class="redeem-container">
    <redemption-submit :value="showRedemptionDialog" />

    <div class="d-flex justify-center flex-column flex-sm-row">
      <group-select
        v-model="selectedCurrency"
        :items="filteredCurrencies"
        label="Select Currency"
        @select="selectCurrency"
      />
    </div>

    <v-form ref="form" v-model="isFormValid" class="redeem-form">
      <div class="mt-3">
        <span class="input-label">Address:</span>
        <v-text-field
          ref="addressInput"
          v-model="model.address"
          :rules="[rules.required(), (value) => addressValidation.test(value) || 'Invalid address']"
          class="text-input address-input"
          placeholder="Please enter your address"
          height="42"
          autocomplete="wallet-address"
        />
      </div>

      <div>
        <span class="input-label">
          {{ maxCoinsAmountText }}
        </span>
        <v-text-field
          ref="amountInput"
          v-model.number="model.amount"
          :rules="[rules.required(), maxBalance, rules.min(minAmount), kycError]"
          class="text-input amount-input"
          height="42"
          type="number"
          inputmode="numeric"
        >
          <template #append>
            <v-btn class="max-button" height="42" width="66" @click="setAllBalance">MAX</v-btn>
          </template>
        </v-text-field>
      </div>

      <div class="limits-wrapper">
        <ExclamationIcon />
        <div class="limit-column">
          <span>Min Redemption</span>
          <b>${{ minAmount }}</b>
        </div>
        <div class="limit-column">
          <span>Max Available</span>
          <b>${{ formattedMMCBalance }}</b>
        </div>
      </div>
    </v-form>

    <div class="estimated-wrapper">
      <div class="estimated-row">
        <span>Estimated Redemption Fees</span>
        <b>{{ estimatedFeeAmount || 0 }} {{ selectedCurrency.text }}</b>
      </div>
      <div class="estimated-row">
        <span>Estimated Redemption</span>
        <div class="d-flex">
          <b class="mr-1" style="margin-top: 1px">{{ estimatedAmount || 0 }} {{ selectedCurrency.text }}</b>
          <InformationCircle @click="requirementsDialog = true" />
        </div>
      </div>
    </div>

    <button class="redeem-button mt-6" @click="submit" :disabled="isWithdrawButtonDisabled">
      <span>Withdraw</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 19 18" fill="none">
        <path
          d="M8.31308 13.7411L12.4599 9.59427C12.8376 9.21661 12.8376 8.60199 12.4599 8.22434L8.31308 4.0775C7.96505 3.72946 7.39486 3.72946 7.04682 4.0775L6.80245 4.32187C6.45441 4.66991 6.45441 5.2401 6.80245 5.58813L9.43865 8.22434C9.81631 8.60199 9.81631 9.21661 9.43865 9.59427L6.80245 12.2305C6.45441 12.5785 6.45441 13.1487 6.80245 13.4967L7.04682 13.7411C7.39486 14.0891 7.96505 14.0891 8.31308 13.7411Z"
          fill="#1C0630"
        />
      </svg>
    </button>

    <div class="info-row mt-6">
      <span>If you have trouble placing your order, contact our support.</span>
      <ChatIcon @click="openChat" />
    </div>
    <div class="info-row mt-4">
      <router-link to="/tnc" class="link">Terms and Conditions</router-link>
      &nbsp;Apply. Please read for more information.
    </div>

    <redemption-requirements v-model="requirementsDialog" />
  </div>
</template>

<style lang="scss" scoped>
  ::v-deep .grouped-menu-btn {
    box-shadow: none !important;
    border-radius: 6px;

    .selected-item {
      gap: 6px;
      font-weight: 500;
    }
  }
  .redeem-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .gradient-tooltip {
    background: linear-gradient(90deg, var(--v-main-lighter-base) 0%, var(--v-main-light-base) 100%);
    border-radius: 10px;
    color: white;
    padding: 12px;
    max-width: 400px;
  }

  .link {
    text-decoration: underline;
    font-weight: 600;
  }

  .redeem-button.border-doubled.large.green {
    border-radius: 30px !important;

    &:before,
    &:after {
      border-radius: 28px !important;
    }
  }

  .with-star::after {
    content: '*';
    color: red;
  }

  .max-button.v-btn {
    background-color: #0cbc47;
    border-radius: 0 5px 5px 0;
    box-shadow: none;

    &::v-deep .v-btn__content {
      color: #fff;
      font-size: 14px;
      font-weight: 700;
      line-height: 120%;
    }
  }

  .redeem-form {
    .input-label {
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
    }

    &::v-deep {
      .text-input {
        padding: 0;

        &.error--text {
          margin-bottom: 10px;
        }

        .v-input__slot {
          min-height: 42px !important;
          display: flex;
          height: 42px;
          gap: 10px;
          flex-shrink: 0;
          border-radius: 6px;
          background: #fff;
          padding-right: 0 !important;
          input {
            padding: 13px 0 13px 20px;
            padding-bottom: 0 !important;
            color: #1c0630;
            font-size: 11px;
            font-weight: 500;
            line-height: 17px;

            &::placeholder {
              color: #1c0630;
            }
          }

          &:before,
          &:after {
            content: none;
          }
        }
      }

      .v-messages__message {
        font-weight: 400;
        font-size: 14px;
        margin-top: 2px;
      }
    }
  }

  //@media screen and (max-width: 992px) {
  //  .redeem-form {
  //    width: 85%;
  //  }
  //
  //  .requirements {
  //    width: 85%;
  //    position: relative;
  //  }
  //}

  @media screen and (max-width: 600px) {
    .redeem-info {
      padding: 0 50px;
    }

    .requirements-helper {
      position: absolute;
      left: 3px;
      top: 3px;
    }

    .requirements {
      padding: 0;
    }

    .requirements-wrapper {
      padding: 15px 25px 10px 25px;
      width: 100%;
      justify-content: space-around !important;
    }
  }

  @media screen and (max-width: 335px) {
    .requirements-wrapper {
      flex-direction: column;
      gap: 10px;
    }
  }

  .limits-wrapper {
    display: flex;
    padding: 8px 10px;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.2);

    svg {
      flex-shrink: 0;
      width: 22px;
      height: 22px;
    }
  }

  .limit-column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    gap: 3px;
    color: #fff;
    text-align: center;
    font-size: 12px;
    line-height: 16px;

    span {
      font-weight: 300;
    }

    b {
      font-weight: 600;
    }
  }

  .estimated-wrapper {
    display: flex;
    flex-direction: column;
  }

  .inner-icon {
    width: 22px;
    height: 22px;
  }

  .estimated-row {
    display: flex;
    padding: 12px 16px;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    color: #fff;
    font-size: 12px;
    font-weight: 400;
    line-height: 120%;

    &:first-child {
      border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    }

    svg {
      cursor: pointer;
      width: 16px;
      height: 16px;
      position: relative;
      left: 2px;
    }
  }

  button.redeem-button {
    display: flex;
    width: 100%;
    height: 42px;
    padding: 3px 10px;
    justify-content: center;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    border-radius: 6px;
    background: #f8bf1e;

    &:hover {
      filter: brightness(104%);
    }

    &:active {
      filter: brightness(97%);
    }

    &[disabled] {
      pointer-events: none;
      filter: grayscale(1);
    }

    span {
      color: #1c0630;
      font-size: 15px;
      font-weight: 600;
      line-height: 100%;
    }
  }

  .info-row {
    display: flex;
    align-items: center;
    color: #fff;
    font-size: 12px;
    font-weight: 400;
    line-height: 150%;

    svg {
      cursor: pointer;
      width: 20px;
      margin-left: 14px;
    }

    .link {
      color: #f8bf1e;
      font-weight: 400;
    }
  }
</style>
